import {
  DECISION_JOURNAL_IN_WEBDOC_LABEL,
  EXISTING_PRESCRIPTION_VALID_UNTIL_LABEL,
  PLANNED_ORDER_DOCUMENTED_LABEL,
  POSITIVE_DECISION_DOCUMENTED_LABEL,
  PRESCRIPTION_CHOICE_LABEL,
  PRESCRIPTION_CHOICES,
  PRESCRIPTION_ISSUED_IN_WEBDOC_LABEL,
  WEBDOC_E_PRESCRIPTION_LABEL,
  REQUISITION_REF_HELP,
  REQUISITION_REF_LABEL,
  WEBDOC_CONTROL_TITLE,
  WEBDOC_JOURNAL_LABEL,
  WEBDOC_LAB_ORDER_LABEL,
  type PrescriptionChoice,
  type PrescriptionDecision,
} from "@care-by-ella/contracts";
import { requisitionRefError } from "@/lib/requisition-ref";
import {
  isERecept,
  JOURNAL_WAITS_FOR_E_RECEPT_HINT,
  journalWaitsForERecept,
} from "@/lib/webdoc-step";

// TES-997 (Ö-259, Ö-247, Ö-255) — the control step
// "Slutför i Webdoc innan du publicerar", shared by every surface that
// publishes a medical decision to the patient: the care meeting, the
// treatment decision, the transfer review and the monthly review. One
// component, so the three choices and the remiss line read the same
// everywhere.
//
// What it shows is decided by the caller:
//   - `prescription` — the three choices, exactly one required, nothing
//     preselected. Only where a prescription is decided (a positive decision,
//     every monthly review).
//   - `journal` — ☐ WEBDOC_JOURNAL_LABEL
//     (TES-1019), first: the care meeting, the treatment decision and the
//     later-recorded prescription (TES-1055), which ask for it on every
//     publish.
//   - `positiveDecision` — TES-1071 (A-02 Steg B), in place of `journal` on a
//     positive treatment decision: the three ticks
//     DECISION_JOURNAL_IN_WEBDOC_LABEL, POSITIVE_DECISION_DOCUMENTED_LABEL and
//     PLANNED_ORDER_DOCUMENTED_LABEL. No caller since TES-1185: a positive
//     decision confirms the prescription in the same publish
//     (`decisionPrescription`), before payment, and the API requires that
//     since TES-1221.
//   - `labOrder` — ☐ WEBDOC_LAB_ORDER_LABEL, only when the
//     completion picker chose "Ta ett nytt blodprov". The picker decides the
//     need; this only confirms the remiss is placed.
//   - `journalLocked` — TES-1061: the journal tick (either form) cannot be
//     set while the caller's note is not copied as it reads now; one already
//     set can still be cleared.
//   - `decisionPrescription` — TES-1185: with `prescription`, the positive
//     treatment decision's two choices only. Registered now, released to
//     her by the verified subscription.
//   - TES-1315 (A-02 2026-10-13, STEG 4) — when the decision offers only
//     `new_issued` (NEW_APPROVAL_CHOICES: every new approval, the direct
//     approval at the first meeting included), the e-recept is not a radio
//     but its own tick ☐ WEBDOC_E_PRESCRIPTION_LABEL. Simon 2026-10-13: the
//     note's "Receptstatus" follows that tick, so it comes first and the
//     journal tick waits for it (the note is final before it is copied).
//     Unticking it later changes the note, and the caller's note-changed
//     logic takes the journal tick back. An existing prescription that
//     still applies (transfer) stays a choice.
// The step publishes nothing itself; the caller's
// "Bekräfta och publicera till patienten" does.

export interface PrescriptionChoiceValue {
  choice: PrescriptionChoice | null;
  existingValidUntil: string;
}

export const EMPTY_PRESCRIPTION_CHOICE: PrescriptionChoiceValue = {
  choice: null,
  existingValidUntil: "",
};

/** The wire shape, or null while nothing is chosen. */
export function prescriptionDecisionPayload(
  v: PrescriptionChoiceValue,
): PrescriptionDecision | null {
  if (!v.choice) return null;
  return {
    choice: v.choice,
    ...(v.choice === "existing_valid" && v.existingValidUntil
      ? { existingValidUntil: v.existingValidUntil }
      : {}),
  };
}

// TES-1185 — the positive decision's two choices and what each does there.
export const DECISION_CHOICES = ["new_issued", "existing_valid"] as const;
export type DecisionPrescriptionChoice = (typeof DECISION_CHOICES)[number];
// TES-1315 — a new approval issues a new e-recept: one tick, no choice.
export const NEW_APPROVAL_CHOICES = ["new_issued"] as const;
const DECISION_CHOICE_LABEL: Record<(typeof DECISION_CHOICES)[number], string> =
  {
    new_issued: PRESCRIPTION_ISSUED_IN_WEBDOC_LABEL,
    existing_valid: PRESCRIPTION_CHOICE_LABEL.existing_valid,
  };
const DECISION_CHOICE_HINT: Record<(typeof DECISION_CHOICES)[number], string> =
  {
    new_issued:
      "Registreras med beslutet. Patienten ser receptet och uthämtningen först när abonnemanget är aktiverat.",
    existing_valid:
      "Registreras med beslutet, utan uthämtningsuppgift. Visas för patienten när abonnemanget är aktiverat.",
  };

// What each choice does, so the doctor chooses between outcomes.
const CHOICE_HINT: Record<PrescriptionChoice, string> = {
  new_issued:
    "Registreras som utfärdat av dig nu. Patienten får uppgiften att hämta ut läkemedlet.",
  existing_valid:
    "Planen publiceras utan nytt recept och utan uthämtningsuppgift.",
  not_yet: "Inget recept registreras.",
};

/** TES-1071 — Steg B's three ticks on a positive decision. */
export interface PositiveDecisionTicks {
  journalDone: boolean;
  decisionDocumented: boolean;
  orderDocumented: boolean;
}

export const EMPTY_POSITIVE_DECISION_TICKS: PositiveDecisionTicks = {
  journalDone: false,
  decisionDocumented: false,
  orderDocumented: false,
};

export function WebdocControlStep({
  idPrefix,
  prescription,
  onPrescriptionChange,
  journal,
  positiveDecision,
  labOrder,
  labOrderDone,
  onLabOrderDoneChange,
  requisitionRef,
  disabled,
  journalLocked = false,
  decisionPrescription = false,
  decisionChoices = DECISION_CHOICES,
}: {
  idPrefix: string;
  decisionPrescription?: boolean;
  // TES-1241 — the decision's choices this caller offers: the transfer
  // review's Recept section has already said which one applies.
  decisionChoices?: readonly DecisionPrescriptionChoice[];
  prescription?: PrescriptionChoiceValue;
  onPrescriptionChange?: (v: PrescriptionChoiceValue) => void;
  journal?: { done: boolean; onChange: (done: boolean) => void };
  positiveDecision?: {
    value: PositiveDecisionTicks;
    onChange: (next: PositiveDecisionTicks) => void;
  };
  labOrder: boolean;
  labOrderDone: boolean;
  onLabOrderDoneChange: (done: boolean) => void;
  /** TES-442 — the optional remiss number, shown under the remiss tick. */
  requisitionRef?: { value: string; onChange: (v: string) => void };
  disabled?: boolean;
  journalLocked?: boolean;
}) {
  const showPrescription = prescription && onPrescriptionChange;
  const eRecept =
    showPrescription && decisionPrescription && isERecept(decisionChoices);
  const journalWaits = journalWaitsForERecept({
    eRecept: !!eRecept,
    eReceptDone: prescription?.choice === "new_issued",
  });
  if (!showPrescription && !labOrder && !journal && !positiveDecision)
    return null;
  return (
    <fieldset
      className="flex flex-col gap-sm rounded-md border border-brand/30 bg-brand-accent/40 p-sm"
      data-testid={`${idPrefix}-webdoc-control`}
    >
      <legend className="px-xs font-display text-[15px] text-textPrimary">
        {WEBDOC_CONTROL_TITLE}
      </legend>
      {showPrescription && !eRecept ? (
        <div
          role="radiogroup"
          aria-label="Receptstatus"
          className="flex flex-col gap-xs"
        >
          {(decisionPrescription ? decisionChoices : PRESCRIPTION_CHOICES).map(
            (c) => (
              <label key={c} className="flex items-start gap-sm text-sm">
                <input
                  type="radio"
                  name={`${idPrefix}-prescription-choice`}
                  className="mt-[3px] accent-brand"
                  checked={prescription.choice === c}
                  onChange={() =>
                    onPrescriptionChange({ ...prescription, choice: c })
                  }
                  disabled={disabled}
                />
                <span>
                  <span className="text-textPrimary">
                    {decisionPrescription && c !== "not_yet"
                      ? DECISION_CHOICE_LABEL[c]
                      : PRESCRIPTION_CHOICE_LABEL[c]}
                  </span>
                  <span className="block text-xs text-textSecondary">
                    {decisionPrescription && c !== "not_yet"
                      ? DECISION_CHOICE_HINT[c]
                      : CHOICE_HINT[c]}
                  </span>
                </span>
              </label>
            ),
          )}
          {prescription.choice === "existing_valid" ? (
            <label className="ml-lg flex flex-col gap-xs text-sm">
              <span className="text-textSecondary">
                {EXISTING_PRESCRIPTION_VALID_UNTIL_LABEL} (om känt)
              </span>
              <input
                type="date"
                className="w-fit rounded-md border border-surfaceMuted p-xs text-sm"
                value={prescription.existingValidUntil}
                onChange={(e) =>
                  onPrescriptionChange({
                    ...prescription,
                    existingValidUntil: e.target.value,
                  })
                }
                disabled={disabled}
              />
            </label>
          ) : null}
        </div>
      ) : null}
      {eRecept ? (
        <label className="flex items-start gap-sm text-sm">
          <input
            type="checkbox"
            className="mt-[3px] accent-brand"
            checked={prescription.choice === "new_issued"}
            onChange={(e) =>
              onPrescriptionChange({
                choice: e.target.checked ? "new_issued" : null,
                existingValidUntil: "",
              })
            }
            disabled={disabled}
          />
          <span>
            <span className="text-textPrimary">
              {WEBDOC_E_PRESCRIPTION_LABEL}
            </span>
            <span className="block text-xs text-textSecondary">
              {DECISION_CHOICE_HINT.new_issued}
            </span>
          </span>
        </label>
      ) : null}
      {journal ? (
        <label className="flex items-start gap-sm text-sm text-textPrimary">
          <input
            type="checkbox"
            className="mt-[3px] accent-brand"
            checked={journal.done}
            onChange={(e) => journal.onChange(e.target.checked)}
            disabled={
              disabled || ((journalLocked || journalWaits) && !journal.done)
            }
            aria-describedby={
              journalWaits ? `${idPrefix}-journal-waits` : undefined
            }
          />
          <span>
            {WEBDOC_JOURNAL_LABEL}
            {journalWaits ? (
              <span
                id={`${idPrefix}-journal-waits`}
                className="block text-xs text-textSecondary"
              >
                {JOURNAL_WAITS_FOR_E_RECEPT_HINT}
              </span>
            ) : null}
          </span>
        </label>
      ) : null}
      {positiveDecision
        ? (
            [
              ["journalDone", DECISION_JOURNAL_IN_WEBDOC_LABEL],
              ["decisionDocumented", POSITIVE_DECISION_DOCUMENTED_LABEL],
              ["orderDocumented", PLANNED_ORDER_DOCUMENTED_LABEL],
            ] as const
          ).map(([key, label]) => (
            <label
              key={key}
              className="flex items-center gap-sm text-sm text-textPrimary"
            >
              <input
                type="checkbox"
                className="accent-brand"
                checked={positiveDecision.value[key]}
                onChange={(e) =>
                  positiveDecision.onChange({
                    ...positiveDecision.value,
                    [key]: e.target.checked,
                  })
                }
                disabled={
                  disabled ||
                  (key === "journalDone" &&
                    journalLocked &&
                    !positiveDecision.value.journalDone)
                }
              />
              {label}
            </label>
          ))
        : null}
      {labOrder ? (
        <label className="flex items-center gap-sm text-sm text-textPrimary">
          <input
            type="checkbox"
            className="accent-brand"
            checked={labOrderDone}
            onChange={(e) => onLabOrderDoneChange(e.target.checked)}
            disabled={disabled}
          />
          {WEBDOC_LAB_ORDER_LABEL}
        </label>
      ) : null}
      {labOrder && requisitionRef ? (
        <RequisitionRefInput
          id={`${idPrefix}-requisition-ref`}
          value={requisitionRef.value}
          onChange={requisitionRef.onChange}
          disabled={disabled}
        />
      ) : null}
    </fieldset>
  );
}

// TES-442 — "Remissnummer i Webdoc (valfritt)". Optional: the result is
// matched by personnummer without it; with it, straight to this order.
// Proposed copy, awaiting Emelie.
export function RequisitionRefInput({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const error = requisitionRefError(value);
  return (
    <div className="flex flex-col gap-xs pl-lg">
      <label htmlFor={id} className="text-sm text-textPrimary">
        {REQUISITION_REF_LABEL}
      </label>
      <input
        id={id}
        type="text"
        inputMode="text"
        autoComplete="off"
        maxLength={64}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-describedby={`${id}-help`}
        aria-invalid={error ? true : undefined}
        className="w-64 max-w-full rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm text-textPrimary focus:outline-none focus:ring-2 focus:ring-brand/40"
      />
      <p id={`${id}-help`} className="text-xs text-textSecondary">
        {error ?? REQUISITION_REF_HELP}
      </p>
    </div>
  );
}
